import React, { useEffect, useState } from "react";
import { Box, Button, Card, IconButton, Tooltip, Typography } from "@mui/material";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import moment from "moment";
import { qrState, qrEndsAt, formatCountdown } from "../utils/paymentQr";
import { ROW_BUTTON_SX } from "./formStyles";

const COLOR = "#6a1b9a";

/**
 * The KSHEMA payment QR on the View page: the picture with a live countdown, and an "expired" panel once its 5 minutes
 * are over (or the policy is Complete / Cancelled) - the picture and its buttons are gone then, not just greyed.
 *
 * @param {object}   doc          policy.paymentQrScreenshot ({ presignedUrl, fileName, expiresAt } or { expired, expiresAt })
 * @param {string}   policyStatus policy.status
 * @param {Function} onView       open the picture in a tab
 * @param {Function} onDownload   download the picture
 * @param {Function} [nowFn]      clock (tests)
 * @param {boolean}  [compact]    opt-in: the size and labelled buttons of the View page's other file rows
 */
const PaymentQrCard = ({ doc, policyStatus, onView, onDownload, nowFn = Date.now, compact = false }) => {
  const [now, setNow] = useState(() => nowFn());
  const { state, remainingMs } = qrState(doc, policyStatus, now);

  // Tick once a second, only while there is something to count down.
  useEffect(() => {
    if (state !== "live") return undefined;
    const timer = setInterval(() => setNow(nowFn()), 1000);
    return () => clearInterval(timer);
  }, [state, nowFn]);

  const ended = qrEndsAt(doc);
  const urgent = state === "live" && remainingMs <= 60 * 1000;
  const color = compact ? "#1E3A5F" : COLOR;

  return (
    <Card
      data-testid="payment-qr-card"
      sx={{ p: compact ? 1 : 2, border: compact ? "1px solid #EAECF0" : `1px solid ${COLOR}33`, borderRadius: compact ? "8px" : undefined, backgroundColor: compact ? "#fff" : "#f6f0fa", boxShadow: "none", height: "100%" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: compact ? "wrap" : "nowrap", gap: compact ? 1 : 1.5 }}>
        <Box
          sx={{
            width: compact ? 36 : 44,
            height: compact ? 36 : 44,
            borderRadius: compact ? "8px" : "10px",
            backgroundColor: compact ? "#EEF3F9" : "#fff",
            border: compact ? "none" : `1px solid ${COLOR}33`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <QrCode2Icon sx={{ color, fontSize: compact ? 20 : undefined }} />
        </Box>
        <Box sx={{ flex: compact ? "1 1 110px" : 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#101828" }}>Payment QR</Typography>
          <Typography sx={{ fontSize: 12, color: "#667085" }}>
            {state === "live" ? "Scan to pay - valid for 5 minutes" : "Kshema General Insurance"}
          </Typography>
        </Box>
        {state === "live" && compact && (
          <Box sx={{ display: "flex", gap: 0.75, flexShrink: 0 }}>
            <Button aria-label="View Payment QR" startIcon={<OpenInNewIcon />} onClick={onView} sx={ROW_BUTTON_SX}>
              View
            </Button>
            <Button aria-label="Download Payment QR" startIcon={<DownloadIcon />} onClick={onDownload} sx={ROW_BUTTON_SX}>
              Download
            </Button>
          </Box>
        )}
        {state === "live" && !compact && (
          <>
            <Tooltip title="View Payment QR">
              <IconButton size="small" onClick={onView} sx={{ color: COLOR }}>
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download Payment QR">
              <IconButton size="small" onClick={onDownload} sx={{ color: COLOR }}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {state === "live" && (
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.25 }}>
          {doc.presignedUrl ? (
            <Box
              component="img"
              src={doc.presignedUrl}
              alt="Payment QR for Kshema General Insurance"
              sx={{ width: "100%", maxWidth: 300, borderRadius: "14px", display: "block" }}
            />
          ) : (
            <Typography sx={{ fontSize: 13, color: "#667085" }}>The QR picture could not be loaded - refresh the page.</Typography>
          )}
          <Box
            role="timer"
            aria-live="off"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.5,
              py: 0.5,
              borderRadius: "999px",
              fontSize: 13,
              fontWeight: 700,
              color: urgent ? "#b3261e" : "#b45309",
              backgroundColor: urgent ? "#fdecea" : "#fff4e5",
              border: `1px solid ${urgent ? "#f2c9c4" : "#ffd9a1"}`,
            }}
          >
            <TimerOutlinedIcon sx={{ fontSize: 17 }} />
            Expires in {formatCountdown(remainingMs)}
          </Box>
        </Box>
      )}

      {state !== "live" && (
        <Box
          data-testid="payment-qr-ended"
          sx={{
            mt: 2,
            py: 3,
            px: 2,
            borderRadius: "14px",
            textAlign: "center",
            backgroundColor: "#fff",
            border: "1px dashed #c9b6dc",
          }}
        >
          <QrCodeScannerIcon sx={{ fontSize: 40, color: "#b8a6c9" }} />
          <Typography sx={{ mt: 0.5, fontWeight: 700, fontSize: 15, color: "#475467" }}>
            {state === "completed" ? "QR no longer needed" : "QR expired"}
          </Typography>
          <Typography sx={{ mt: 0.25, fontSize: 12.5, color: "#667085" }}>
            {state === "completed"
              ? "This policy is finished, so the payment QR is not shown any more."
              : ended
                ? `It was valid until ${moment(ended).format("hh:mm A, DD MMM YYYY")}. Run the payment step again for a new QR.`
                : "Run the payment step again for a new QR."}
          </Typography>
        </Box>
      )}
    </Card>
  );
};

export default PaymentQrCard;
